/* eslint-disable no-param-reassign */
import { Button } from '@getro/rombo';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import { useLazySearchWithAiQuery } from 'services/contactsV2';
import { useHistory } from 'react-router-dom';
import { useContactsSearchParam } from 'pages/contactsExtended/hooks/useContactsSearchParam';
import { reverseParsedFilters } from 'services/contacts/filters';
import PropTypes from 'prop-types';
import { defaultFilters } from 'components/organisms/contactsFilters/defaultFilters';
import { trackEvent } from 'helpers/analytics';
import { useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';

import IconStars from '../../../assets/icon-stars.svg';

export const AIFilters = ({ setFilters }) => {
  const [value, setValue] = useState(new URLSearchParams(window.location.search).get('aiFilters') || '');
  const [showInput, setShowInput] = useState(!!value);
  const [searchWithAi, { isFetching }] = useLazySearchWithAiQuery();
  const history = useHistory();
  const [queryParams] = useContactsSearchParam();
  const [hasError, setHasError] = useState(false);
  const { id: collectionId } = useSelector(activeNetworkSelector);

  const onEnter = async () => {
    if (!value) {
      setValue('');
      history.push({
        search: '',
      });
      setFilters(defaultFilters);
      return;
    }

    setHasError(false);
    const search = window.location.search
      .replace(/^\?/, '')
      .split('&')
      .filter((param) => param.split('=')[0] !== 'aiFilters')
      .join('&');

    history.push({
      search: value ? `${search}${search ? '&' : '?'}page=1&aiFilters=${value}` : search,
    });

    trackEvent('contacts:natural_language_query', {
      query: value,
      collectionId,
    });

    const { data, error } = await searchWithAi({ query: value, queryParams });

    if (error || !Object.keys(data?.filters || {}).length) {
      setHasError(true);
      return;
    }
    const filters = reverseParsedFilters(data.filters);

    dispatchEvent(new Event('openFiltersPreferences'));
    const preferenceFilters = new Set([
      'signupHasResume',
      'signupDaysSinceJoined',
      'signupJobSearchStatuses',
      'signupSeniorities',
      'signupWorkEnvironment',
      'signupLocations',
      'signupJobSearchStatuses',
    ]);

    const hasPreferenceFilters = Object.keys(filters).some((key) => preferenceFilters.has(key));

    if (hasPreferenceFilters) {
      dispatchEvent(new Event('openFiltersPreferences'));
    }

    setFilters(filters, { resetPreviousFilters: true });
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }

    window.addEventListener('resetFilters', () => {
      setValue('');
      setHasError(false);
      history.push({
        search: '',
      });
    });
  }, [history]);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        borderRadius: '4px',
        position: 'relative',
        zIndex: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '4px',
          padding: '1px',
          background: 'linear-gradient(118deg, #FF9F8A -21.79%, #F4F 27.75%, #4900FF 112.28%)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          overflow: 'hidden',
        },
      }}
    >
      <Flex sx={{ width: '100%' }}>
        <Flex
          data-testid="filter-title-trigger"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            cursor: 'pointer',
            zIndex: 2,
            position: 'relative',
            p: '7px',
            m: '1px',
            borderRadius: '4px',
            ':hover': {
              bg: !showInput ? 'neutral.20' : 'transparent',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowInput(!showInput);

            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.selectionStart = inputRef.current.value.length;
                inputRef.current.selectionEnd = inputRef.current.value.length;
              }
            }, 0);
          }}
        >
          <Flex width="100%" sx={{ alignItems: 'center', gap: '8px' }}>
            <Image src={IconStars} width="16px" height="16px" alt="Stars" />
            <Text flexGrow="1" as="p" fontSize="14px" fontWeight="500" lineHeight="1.4" color="text.dark">
              Start filtering with AI
            </Text>
            <Box as={showInput ? ChevronDown : ChevronRight} size="16px" sx={{ color: 'text.subtle' }} />
          </Flex>
        </Flex>
      </Flex>
      {showInput && (
        <Flex flexDirection="column" px="8px" pb="8px" sx={{ gap: '8px', position: 'relative' }}>
          <Text fontSize="14px" lineHeight="1.4" color="text.subtle">
            Describe what you need and let our AI set the right filters.
          </Text>
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            <Flex
              ref={inputRef}
              placeholder="Example: Product managers working in the AI department"
              as="textarea"
              value={value}
              autoFocus
              data-testid="search-with-ai-filter-input"
              onChange={({ target }) => {
                setValue(target.value);
                if (target.value.length > 0) {
                  target.style.height = `${target.scrollHeight}px`;
                } else {
                  target.style.height = '56px';
                }
              }}
              onKeyDown={({ target, key }) => {
                if (key === 'Enter' && target.value.trim()) {
                  onEnter();
                }
              }}
              wrap="soft"
              sx={{
                '&::placeholder': {
                  color: 'text.subtle',
                  fontSize: '14px',
                  lineHeight: '19.6px',
                },
                fontSize: '14px',
                padding: '8px',
                color: 'text.main',
                outline: 'none',
                borderColor: 'neutral.400',
                borderRadius: '4px',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                height: '72px',
                minHeight: '72px',
                resize: 'none',
                overflow: 'hidden',
              }}
            />
          </Flex>

          <Button
            size="small"
            loading={isFetching}
            onClick={() => onEnter()}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',

              '&:disabled:not(.ladda-button)': {
                cursor: 'not-allowed',
                bg: 'neutral.300',
              },
            }}
          >
            Search with AI
          </Button>
          {hasError && (
            <Box sx={{ color: 'text.error', fontSize: '14px', lineHeight: '1.4' }}>
              Sorry, we didn’t get that. Try rephrasing your search or set the filters below.
            </Box>
          )}
        </Flex>
      )}
    </Flex>
  );
};

AIFilters.propTypes = {
  setFilters: PropTypes.func.isRequired,
};
